import React, { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { fetchTermsOfService, fetchPrivacyPolicy } from "../services/organisations.service";

const STEPS = ["Terms & Conditions", "Privacy Policy"];

const MD_STYLES = {
  p: 3,
  overflowY: "auto",
  width: "100%",
  height: "100%",
  color: "#374151",
  fontSize: "0.9375rem",
  lineHeight: 1.75,
  "& h1": {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: "#111827",
    mt: 3,
    mb: 1.5,
  },
  "& h2": {
    fontSize: "1.25rem",
    fontWeight: 700,
    color: "#111827",
    mt: 3,
    mb: 1,
    pb: 0.5,
    borderBottom: "1px solid #F3F4F6",
  },
  "& h3": {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: "#111827",
    mt: 2.5,
    mb: 0.75,
  },
  "& p": { my: 1.25 },
  "& ul, & ol": { pl: 3, my: 1.25 },
  "& li": { mb: 0.5 },
  "& a": {
    color: "#1976D2",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
  "& strong": { fontWeight: 600, color: "#111827" },
  "& hr": { border: 0, borderTop: "1px solid #E5E7EB", my: 3 },
  "& > :first-of-type": { mt: 0 },
} as const;

const LoadingSkeleton = () => (
  <Stack spacing={1.5} sx={{ width: "100%", p: 3 }}>
    <Skeleton variant="text" width="55%" height={32} />
    <Skeleton variant="text" width="35%" height={20} />
    <Box sx={{ height: 8 }} />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="text" width="96%" />
    <Skeleton variant="text" width="92%" />
    <Skeleton variant="text" width="98%" />
    <Box sx={{ height: 8 }} />
    <Skeleton variant="text" width="45%" height={26} />
    <Skeleton variant="text" width="100%" />
    <Skeleton variant="text" width="90%" />
  </Stack>
);

interface DocState {
  markdown: string;
  hash: string;
  loading: boolean;
  error: string;
}

const EMPTY_DOC: DocState = {
  markdown: "",
  hash: "",
  loading: false,
  error: "",
};

const hashText = async (text: string): Promise<string> => {
  const encoded = new TextEncoder().encode(text);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encoded);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

interface TermsOfServiceModalProps {
  open: boolean;
  onClose: () => void;
  onAccept: (hashes: { tosHash: string; ppHash: string }) => void;
  loading?: boolean;
  disableDismiss?: boolean;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
  open,
  onClose,
  onAccept,
  loading = false,
  disableDismiss = false,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [tos, setTos] = useState<DocState>(EMPTY_DOC);
  const [pp, setPp] = useState<DocState>(EMPTY_DOC);
  const [agreedTos, setAgreedTos] = useState(false);
  const [agreedPp, setAgreedPp] = useState(false);

  const fetchDoc = async (
    fetcher: () => Promise<unknown>,
    setter: React.Dispatch<React.SetStateAction<DocState>>,
  ) => {
    setter((s) => ({ ...s, loading: true, error: "" }));
    try {
      const res = (await fetcher()) as string;
      const text = typeof res === "string" ? res : "";
      const hash = await hashText(text);
      setter({ markdown: text, hash, loading: false, error: "" });
    } catch {
      setter((s) => ({
        ...s,
        loading: false,
        error: "Failed to load document. Please try again later.",
      }));
    }
  };

  // Reset state and load T&C whenever the modal opens
  useEffect(() => {
    setActiveStep(0);
    setAgreedTos(false);
    setAgreedPp(false);
    setTos(EMPTY_DOC);
    setPp(EMPTY_DOC);
    fetchDoc(fetchTermsOfService, setTos);
  }, [open]);

  const handleNext = () => {
    setActiveStep(1);
    if (!pp.markdown) fetchDoc(fetchPrivacyPolicy, setPp);
  };

  const handleBack = () => setActiveStep(0);

  const handleAccept = () => {
    onAccept({ tosHash: tos.hash, ppHash: pp.hash });
  };

  const renderDocViewer = (doc: DocState) => {
    if (doc.loading) return <LoadingSkeleton />;
    if (doc.error)
      return (
        <Alert severity="error" sx={{ m: 2 }}>
          {doc.error}
        </Alert>
      );
    if (!doc.markdown) return null;
    return (
      <Box sx={MD_STYLES}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {doc.markdown}
        </ReactMarkdown>
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={disableDismiss ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      disableEscapeKeyDown={disableDismiss}
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>Review & Accept Documents</DialogTitle>

      <DialogContent
        dividers
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          p: 2,
        }}
      >
        {/* ── Stepper — centred with breathing room ── */}
        <Box sx={{ maxWidth: 480, mx: "auto", width: "100%", mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          {activeStep === 0
            ? "Please review the Terms of Service to continue."
            : "Please review and accept the Privacy Policy to continue."}
        </Typography>

        {/* ── Document viewer ── */}
        <Box
          sx={{
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            mb: 2,
            flex: 1,
            minHeight: 0,
            background: "#fafbfc",
            display: "flex",
            justifyContent: "center",
            alignItems:
              activeStep === 0 && tos.loading ? "center" : "flex-start",
            overflow: "auto",
          }}
        >
          {activeStep === 0 ? renderDocViewer(tos) : renderDocViewer(pp)}
        </Box>

        {/* ── Agreement checkbox ── */}
        {activeStep === 0 ? (
          /* Step 1: must be checked to enable Next */
          <FormControlLabel
            sx={{ alignItems: "flex-start", mt: 1 }}
            control={
              <Checkbox
                checked={agreedTos}
                onChange={(e) => setAgreedTos(e.target.checked)}
                color="primary"
                disabled={tos.loading || loading}
                className="terms-checkbox"
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.5, color: "text.secondary" }}
              >
                I am a <b>licensed financial planning professional</b> and I
                have read and agree to the <b>Prospo AI Terms of Service</b>. I
                understand that I am responsible for verifying the accuracy of
                all AI-generated content and that my data will be permanently
                deleted 30 days after account termination.
              </Typography>
            }
          />
        ) : (
          /* Step 2: must be checked to Proceed */
          <FormControlLabel
            sx={{ alignItems: "flex-start", mt: 1 }}
            control={
              <Checkbox
                checked={agreedPp}
                onChange={(e) => setAgreedPp(e.target.checked)}
                color="primary"
                disabled={loading || pp.loading}
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
                I have read and agree to the <b>Prospo AI Privacy Policy</b>.
              </Typography>
            }
          />
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
        {/* Left side */}
        {activeStep === 0 ? (
          <Box /> // spacer so right-side buttons stay right
        ) : (
          <Button color="inherit" onClick={handleBack} disabled={loading}>
            Back
          </Button>
        )}

        {/* Right side */}
        <Box sx={{ display: "flex", gap: 1 }}>
          {!disableDismiss && (
            <Button onClick={onClose} color="secondary" disabled={loading}>
              Cancel
            </Button>
          )}

          {activeStep === 0 ? (
            <Button
              onClick={handleNext}
              color="primary"
              variant="contained"
              disabled={!agreedTos || tos.loading}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={handleAccept}
              color="primary"
              variant="contained"
              disabled={!agreedPp || loading || pp.loading || !pp.hash}
            >
              {loading ? "Processing…" : "Proceed"}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfServiceModal;
